<template>
  <aside class="readonly-inspector">
    <h6 class="title">Designer details</h6>

    <div class="section">
      <div class="label">Canvas</div>
      <div class="value">
        {{ fmt(designerStore.canvasSizeInMm.width, 0) }} x
        {{ fmt(designerStore.canvasSizeInMm.height, 0) }} mm
      </div>
    </div>

    <div class="section">
      <div class="label">Die-cut mask</div>
      <div class="value">
        {{ dieCutLabel }} · {{ fmt(designerStore.dieCutMask.width, 0) }} x
        {{ fmt(designerStore.dieCutMask.height, 0) }} mm
      </div>
      <div class="subvalue" v-if="isRectangle">
        Radius: {{ fmt(designerStore.dieCutMask.radiusX, 0) }} /
        {{ fmt(designerStore.dieCutMask.radiusY, 0) }} mm
      </div>
      <div class="subvalue">
        Bleed: {{ fmt(designerStore.dieCutMask.bleedOffset, 0) }} mm
      </div>
    </div>

    <div class="section">
      <div class="label">Label image</div>
      <div class="value" v-if="designerStore.labelImage.imageSrc">
        {{ fmt(designerStore.labelImage.width, 0) }} x
        {{ fmt(designerStore.labelImage.height, 0) }} mm ·
        pos {{ fmt(designerStore.labelImage.positionX, 0) }}/{{ fmt(designerStore.labelImage.positionY, 0) }} ·
        {{ designerStore.labelImage.fit }}
      </div>
      <div class="value muted" v-else>No image</div>
    </div>

    <div class="section">
      <div class="label">Text layers ({{ designerStore.textLayers.length }})</div>
      <div class="layers" v-if="designerStore.textLayers.length">
        <div class="layer" v-for="(layer, index) in designerStore.textLayers" :key="layer.id">
          <div class="layer-title">Layer {{ index + 1 }}</div>
          <div class="layer-text">“{{ layer.text || '—' }}”</div>
          <div class="layer-meta">
            {{ layer.font }} · {{ layer.fontSize }}pt · {{ layer.fontWeight }}
          </div>
          <div class="layer-meta">
            {{ layer.color }} · {{ layer.align }} ·
            {{ fmt(layer.width, 0) }} x {{ fmt(layer.height, 0) }} mm ·
            pos {{ fmt(layer.positionX, 0) }}/{{ fmt(layer.positionY, 0) }}
          </div>
        </div>
      </div>
      <div class="value muted" v-else>No text layers</div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useDesignerStore } from '@/stores/designerStore'

const designerStore = useDesignerStore()

const dieCutLabel = computed(() => {
  const type = designerStore.dieCutMask.type
  if (type === 'circle') return 'Circle'
  if (type === 'triangle') return 'Triangle'
  return 'Rectangle'
})

const isRectangle = computed(() => designerStore.dieCutMask.type === 'rectangle')

function fmt(value: number, digits = 0) {
  return Number.isFinite(value) ? value.toFixed(digits) : '-'
}
</script>

<style scoped lang="scss">
.readonly-inspector {
  background: #ffffff;
  border-radius: 32px;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  padding: 24px;
}

.title {
  font-size: 16px;
  font-weight: 700;
  color: #111;
  margin-bottom: 16px;
}

.section {
  margin-bottom: 16px;
}

.label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b6b6b;
  margin-bottom: 6px;
}

.value,
.subvalue,
.layer-meta,
.layer-text {
  font-size: 13px;
  color: #111;
}

.subvalue {
  color: #4b4b4b;
}

.muted {
  color: #8a8a8a;
}

.layers {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.layer {
  padding: 10px 12px;
  border-radius: 16px;
  background: #faf8f2;
}

.layer-title {
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 4px;
}
</style>
